import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Breadcrumbs, Card, GridTable } from '@diamond/shared/ui';
import { showToast } from '@diamond/shared/ui';
import { adminCaptureException } from '@diamond/shared/utils';
import { AdminLayout } from '@diamond/sol-admin/components';
import { getApiErrorMessage } from '@diamond/sol-admin/utils';
import type { RowDragEndEvent } from 'ag-grid-community';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getLHCItemsAsRows } from '../api';
import {
  LHCDetailEmptyState,
  LHCDetailFilterEmptyState,
} from '../components/lhc-detail-empty-state';
import { LHCDetailFilterBar } from '../components/lhc-detail-filter-bar';
import { LHCDetailHeader } from '../components/lhc-detail-header';
import { LHCDetailSummary } from '../components/lhc-detail-summary';
import {
  buildLHCDetailColumnDefs,
  getRowStyle,
} from '../components/lhc-detail-table';
import { LHCMutationHistoryDrawer } from '../components/lhc-mutation-history-drawer';
import { RemarkLHCModal } from '../components/remark-lhc-modal';
import { SearchNomorMutasiModal } from '../components/search-nomor-mutasi-modal';
import { MutationDetailDrawer } from '../components/search-nomor-mutasi-modal/mutation-modal';
import { SearchSuratJalanModal } from '../components/search-surat-jalan-modal';
import { PLANT_OPTIONS } from '../constants';
import {
  useAllocateLHCItemMutationsMutation,
  useCollectionBiltoDetail,
  useCollectionMutationDetailMutation,
  useCollectionPlants,
  useDeleteLHCItemMutation,
  useLHCDetail,
  useReorderLHCItemMutation,
  useReplaceLHCItemMutationsMutation,
} from '../hooks';
import { useLhcDetailFilters } from '../hooks/use-lhc-detail-filters';
import { useLhcDetailModals } from '../hooks/use-lhc-detail-modals';
import type {
  LHCDetailInvoiceRowData,
  SearchNomorMutasiSelectionPayload,
} from '../types';
import { getDateRangeByPeriod } from '../utils/date-filter-period';
import {
  getMutationDetailRequestFromRow,
  getSelectedMutationFromRow,
  getVisibleLhcDetailRows,
} from '../utils/lhc-detail-derived';
import {
  type MutationHistoryFilterLabel,
  mapMutationHistoryFilterToType,
  toApiDateFromUiValue,
} from '../utils/lhc-mutation-history';

const links = [
  { title: 'Beranda', link: '/' },
  { title: 'Collection Management', link: '/collection-management' },
  { title: 'Detail LHC', link: '#' },
];

const TABLE_HEIGHT = 'clamp(400px, calc(100vh - 420px), 720px)';
const TABLE_MIN_HEIGHT = '320px';
const TABLE_EMPTY_HEIGHT = '100px';

type ApiErrorPayload = {
  message?: string;
  statusCode?: number;
  error?: string;
  [key: string]: unknown;
};

function setTruthyTags(
  tags: Record<string, string | number | undefined>
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(tags).filter(
      ([, value]) => value !== undefined && value !== ''
    )
  ) as Record<string, string | number>;
}

function captureLhcDetailMutationError({
  error,
  lhcId,
  lhcNo,
  mutationId,
  mutationNo,
  rowData,
}: {
  error: unknown;
  lhcId: string;
  lhcNo: string;
  mutationId: string | null;
  mutationNo: string | null;
  rowData: LHCDetailInvoiceRowData | null;
}) {
  const isAxios = isAxiosError<ApiErrorPayload>(error);
  const payloadData = isAxios ? error.response?.data : undefined;
  const statusCode =
    payloadData?.statusCode ?? (isAxios ? error.response?.status : undefined);
  const message =
    (typeof payloadData?.message === 'string' && payloadData.message) ||
    (error instanceof Error ? error.message : 'Unknown error');

  adminCaptureException({ message }, (scope) => {
    scope.setTags(
      setTruthyTags({
        queryKey: 'collection-mutation-detail',
        operation: 'get_mutation_detail',
        lhc_no: lhcNo,
        lhc_id: lhcId,
        mutation_no: mutationNo ?? undefined,
        mutation_id: mutationId ?? undefined,
        status_code: statusCode,
      })
    );
    scope.setContext('collection_mutation', {
      lhc_no: lhcNo,
      lhc_id: lhcId,
      mutation_no: mutationNo,
      mutation_id: mutationId,
      item_id: rowData?.rowId ?? null,
      order_meta_id: rowData?.orderMetaId ?? null,
      delivery_order_no: rowData?.deliveryOrderNo ?? null,
      invoice_no: rowData?.invoiceNo ?? null,
    });
    scope.setContext('error', {
      error: payloadData ?? message,
    });
    scope.setLevel('error');
  });
}

export function LHCDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: collectionPlantsData } = useCollectionPlants();
  const plantOptions = useMemo(() => {
    if (collectionPlantsData?.data?.length) {
      return collectionPlantsData.data.map((p) => ({
        label: p.name,
        value: p.id,
      }));
    }
    return PLANT_OPTIONS;
  }, [collectionPlantsData]);

  const {
    apiParams,
    appliedFilters,
    handleDateFilter,
    handleSearch,
    methods,
    plantFilter,
    searchValue,
    setPlantFilter,
    setSearchValue,
    updateDateParams,
  } = useLhcDetailFilters();

  const {
    data: detailData,
    isLoading,
    refetch: refetchLHCDetail,
  } = useLHCDetail(id || '', apiParams);
  const {
    closeMutationHistoryDrawer,
    closePaymentProofDrawer,
    closeRemarkModal,
    closeSearchModal,
    closeSearchMutasiModal,
    isMutationHistoryDrawerOpen,
    isPaymentProofDrawerOpen,
    isRemarkModalOpen,
    isSearchModalOpen,
    isSearchMutasiModalOpen,
    mutasiModalLabels,
    mutasiModalMode,
    mutationHistoryEndDate,
    mutationHistoryFilter,
    mutationHistoryLimit,
    mutationHistoryPage,
    mutationHistoryStartDate,
    openAddMutation,
    openChangeMutation,
    openMutationHistoryDrawer,
    openPaymentProofDrawer,
    openRemarkModal,
    openSearchModal,
    openSearchMutation,
    selectedBankMutationIdForDetail,
    selectedBiltoForHistoryDrawer,
    selectedRowForDrawer,
    selectedRowForMutasi,
    selectedRowForRemark,
    setMutationHistoryEndDate,
    setMutationHistoryFilter,
    setMutationHistoryLimit,
    setMutationHistoryPage,
    setMutationHistoryStartDate,
  } = useLhcDetailModals();
  const [itemToDelete, setItemToDelete] =
    useState<LHCDetailInvoiceRowData | null>(null);
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const selectedMutationForDetailRef = useRef<{
    mutationId: string | null;
    mutationNo: string | null;
    lhcNo: string;
    rowData: LHCDetailInvoiceRowData | null;
  }>({
    mutationId: null,
    mutationNo: null,
    lhcNo: '',
    rowData: null,
  });

  const deleteItemMutation = useDeleteLHCItemMutation({
    onSuccess: () => {
      showToast(toast, 'success', 'Item LHC berhasil dihapus');
      onDeleteDialogClose();
      setItemToDelete(null);
    },
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal menghapus item LHC');
    },
  });
  const [isDragMode, setIsDragMode] = useState(false);
  const reorderMutation = useReorderLHCItemMutation({
    onSuccess: () => {
      showToast(toast, 'success', 'Urutan berhasil diubah');
      setIsDragMode(false);
      void refetchLHCDetail();
    },
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal mengubah urutan');
      setIsDragMode(false);
    },
  });
  const allocateMutation = useAllocateLHCItemMutationsMutation();
  const replaceMutation = useReplaceLHCItemMutationsMutation();
  const mutationDetailMutation = useCollectionMutationDetailMutation({
    onError: (error) => {
      const detailRequest = selectedMutationForDetailRef.current;
      captureLhcDetailMutationError({
        error,
        lhcId: id || '',
        lhcNo: detailRequest.lhcNo,
        mutationId: detailRequest.mutationId,
        mutationNo: detailRequest.mutationNo,
        rowData: detailRequest.rowData,
      });
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal memuat detail mutasi', {
          includeStatusCode: true,
        })
      );
    },
  });

  const tableData = useMemo<LHCDetailInvoiceRowData[]>(() => {
    if (!detailData) return [];
    return getLHCItemsAsRows(detailData);
  }, [detailData]);

  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((rowId: string) => {
    setCollapsedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const visibleRows = useMemo<LHCDetailInvoiceRowData[]>(() => {
    return getVisibleLhcDetailRows(tableData, collapsedRows);
  }, [tableData, collapsedRows]);

  const selectedMutationForDrawer = useMemo(() => {
    return getSelectedMutationFromRow(
      selectedRowForDrawer,
      selectedBankMutationIdForDetail
    );
  }, [selectedRowForDrawer, selectedBankMutationIdForDetail]);

  const mutationHistoryType = useMemo(
    () => mapMutationHistoryFilterToType(mutationHistoryFilter),
    [mutationHistoryFilter]
  );

  const mutationHistoryParams = useMemo(
    () => ({
      page: mutationHistoryPage,
      limit: mutationHistoryLimit,
      start_date: mutationHistoryStartDate || undefined,
      end_date: mutationHistoryEndDate || undefined,
      type: mutationHistoryType,
    }),
    [
      mutationHistoryPage,
      mutationHistoryLimit,
      mutationHistoryStartDate,
      mutationHistoryEndDate,
      mutationHistoryType,
    ]
  );

  const {
    data: mutationHistoryData,
    isLoading: isMutationHistoryLoading,
    isError: isMutationHistoryError,
  } = useCollectionBiltoDetail(
    selectedBiltoForHistoryDrawer ?? '',
    mutationHistoryParams
  );

  const hasInvoices = visibleRows.length > 0;
  const hasActiveFilters =
    !!appliedFilters.dateFrom ||
    !!appliedFilters.dateTo ||
    !!appliedFilters.plantFilter ||
    !!appliedFilters.appliedSearch;

  const handleBack = () => {
    navigate('/collection-management');
  };

  const handleAddSuratJalan = openSearchModal;
  const handleSearchModalClose = closeSearchModal;

  const handleSearchModalAdded = () => {
    void refetchLHCDetail();
  };

  const handleSync = () => {
    void refetchLHCDetail();
  };

  const handleChangeMutation = openChangeMutation;
  const handleAddMutation = openAddMutation;
  const handleEditRemark = openRemarkModal;
  const handleCloseRemarkModal = closeRemarkModal;

  const handleMoveOrder = useCallback(
    (_row: LHCDetailInvoiceRowData) => {
      if (hasActiveFilters) {
        showToast(
          toast,
          'warning',
          'Hapus filter terlebih dahulu untuk mengubah urutan'
        );
        return;
      }
      setIsDragMode(true);
    },
    [hasActiveFilters, toast]
  );

  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<LHCDetailInvoiceRowData>) => {
      if (!id || !event.node.data || event.node.data.isChildRow) return;

      const draggedItemId = event.node.data.rowId;
      const overIndex = event.overIndex;

      if (overIndex == null || overIndex < 0) {
        setIsDragMode(false);
        return;
      }

      let parentIndex = -1;
      for (let i = 0; i <= overIndex && i < visibleRows.length; i++) {
        if (!visibleRows[i].isChildRow) {
          parentIndex++;
        }
      }

      if (parentIndex < 0) {
        setIsDragMode(false);
        return;
      }

      reorderMutation.mutate({
        lhcId: id,
        itemId: draggedItemId,
        index: parentIndex,
      });
    },
    [id, visibleRows, reorderMutation]
  );

  useEffect(() => {
    if (!isDragMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDragMode(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDragMode]);

  const handleDelete = useCallback(
    (row: LHCDetailInvoiceRowData) => {
      setItemToDelete(row);
      onDeleteDialogOpen();
    },
    [onDeleteDialogOpen]
  );

  const handleConfirmDelete = () => {
    if (itemToDelete && id) {
      deleteItemMutation.mutate({ lhcId: id, itemId: itemToDelete.rowId });
    }
  };

  const handleClosePaymentProofDrawer = () => {
    closePaymentProofDrawer();
    selectedMutationForDetailRef.current = {
      mutationId: null,
      mutationNo: null,
      lhcNo: '',
      rowData: null,
    };
    mutationDetailMutation.reset();
  };

  const handleOpenPaymentProofDrawer = useCallback(
    (row: LHCDetailInvoiceRowData, bankMutationId?: string) => {
      openPaymentProofDrawer(row, bankMutationId);

      const request = getMutationDetailRequestFromRow(row, bankMutationId);
      const selectedMutation = getSelectedMutationFromRow(row, bankMutationId);
      selectedMutationForDetailRef.current = {
        mutationId: request?.mutationId ?? null,
        mutationNo: selectedMutation?.mutationNo ?? null,
        lhcNo: detailData?.lhc?.lhc_no ?? '',
        rowData: row,
      };
      if (!request) {
        return;
      }

      mutationDetailMutation.mutate(request);
    },
    [detailData?.lhc?.lhc_no, mutationDetailMutation, openPaymentProofDrawer]
  );

  const handleMutationHistoryFilterChange = useCallback(
    (filter: MutationHistoryFilterLabel) => {
      setMutationHistoryFilter(filter);
      setMutationHistoryPage(1);
    },
    [setMutationHistoryFilter, setMutationHistoryPage]
  );

  const handleMutationHistoryDateFilter = useCallback((period: string) => {
    return getDateRangeByPeriod(period);
  }, []);

  const handleMutationHistoryDateRangeChange = useCallback(
    (fromDate: string, toDate: string) => {
      setMutationHistoryStartDate(toApiDateFromUiValue(fromDate) || '');
      setMutationHistoryEndDate(toApiDateFromUiValue(toDate) || '');
      setMutationHistoryPage(1);
    },
    [
      setMutationHistoryEndDate,
      setMutationHistoryPage,
      setMutationHistoryStartDate,
    ]
  );

  const handleMutationHistoryPageChange = useCallback(
    (page: number) => {
      setMutationHistoryPage(page);
    },
    [setMutationHistoryPage]
  );

  const handleMutationHistoryLimitChange = useCallback(
    (limit: number) => {
      setMutationHistoryLimit(limit);
      setMutationHistoryPage(1);
    },
    [setMutationHistoryLimit, setMutationHistoryPage]
  );

  const handleMutationSelected = useCallback(
    async ({
      bankMutationId,
      mutationNo,
      amountApplied,
    }: SearchNomorMutasiSelectionPayload) => {
      if (!selectedRowForMutasi) return;
      if (!id) {
        throw new Error('LHC tidak ditemukan');
      }

      const payload = {
        lhcId: id,
        itemId: selectedRowForMutasi.rowId,
        lhc_no: detailData?.lhc?.lhc_no,
        mutation_nos: mutationNo ? [mutationNo] : undefined,
        allocations: [
          {
            bank_mutation_id: bankMutationId,
            amount_applied: amountApplied,
          },
        ],
      };

      if (mutasiModalMode === 'change') {
        await replaceMutation.mutateAsync(payload);
      } else {
        await allocateMutation.mutateAsync(payload);
      }
      await refetchLHCDetail();
    },
    [
      allocateMutation,
      detailData?.lhc?.lhc_no,
      replaceMutation,
      id,
      mutasiModalMode,
      refetchLHCDetail,
      selectedRowForMutasi,
    ]
  );

  const handleCloseSearchMutasiModal = closeSearchMutasiModal;

  const columnDefs = useMemo(
    () =>
      buildLHCDetailColumnDefs(
        {
          onChangeMutation: handleChangeMutation,
          onAddMutation: handleAddMutation,
          onEditRemark: handleEditRemark,
          onMoveOrder: handleMoveOrder,
          onDelete: handleDelete,
          onViewPaymentProof: handleOpenPaymentProofDrawer,
          onSearchMutation: openSearchMutation,
          onViewMutationHistory: openMutationHistoryDrawer,
          onOpenMutationDetail: handleOpenPaymentProofDrawer,
        },
        { isDragMode }
      ),
    [
      handleAddMutation,
      handleChangeMutation,
      handleDelete,
      handleEditRemark,
      handleMoveOrder,
      handleOpenPaymentProofDrawer,
      isDragMode,
      openMutationHistoryDrawer,
      openSearchMutation,
    ]
  );

  const lhc = detailData?.lhc ?? null;

  return (
    <AdminLayout maxWidth="100%">
      <Box paddingBottom={4}>
        <Breadcrumbs links={links} useRouterDom />
      </Box>

      <Box py={4}>
        {lhc && (
          <>
            <LHCDetailHeader
              lhc={lhc}
              onBack={handleBack}
              onSync={handleSync}
            />
            <LHCDetailSummary lhc={lhc} />
          </>
        )}

        <LHCDetailFilterBar
          methods={methods}
          handleDateFilter={handleDateFilter}
          updateDateParams={updateDateParams}
          plantValue={plantFilter}
          onPlantChange={setPlantFilter}
          plantOptions={plantOptions}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearch={handleSearch}
          onAddSuratJalan={handleAddSuratJalan}
        />

        {isDragMode && (
          <Flex
            align="center"
            justify="space-between"
            bg="blue.50"
            px={4}
            py={2}
            borderRadius="md"
            mb={2}
          >
            <Text fontSize="sm" color="blue.700">
              Drag baris untuk mengubah urutan. Tekan Escape untuk batal.
            </Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsDragMode(false)}
            >
              Batal
            </Button>
          </Flex>
        )}

        <Card p="0" m="0">
          <GridTable<LHCDetailInvoiceRowData>
            columnDefs={columnDefs}
            rowData={visibleRows}
            isLoading={isLoading || reorderMutation.isPending}
            pagination={false}
            height={hasInvoices ? TABLE_HEIGHT : TABLE_EMPTY_HEIGHT}
            minHeight={hasInvoices ? TABLE_MIN_HEIGHT : TABLE_EMPTY_HEIGHT}
            gridOptions={{
              defaultColDef: {
                resizable: true,
                sortable: !isDragMode,
              },
              domLayout: 'normal',
              suppressNoRowsOverlay: true,
              getRowStyle,
              context: { toggleExpand },
              rowDragManaged: isDragMode,
              suppressMoveWhenRowDragging: true,
              onRowDragEnd: isDragMode ? handleRowDragEnd : undefined,
            }}
          />
        </Card>

        {!hasInvoices && !isLoading && hasActiveFilters && (
          <LHCDetailFilterEmptyState />
        )}

        {!hasInvoices && !isLoading && !hasActiveFilters && (
          <LHCDetailEmptyState onSearchSuratJalan={handleAddSuratJalan} />
        )}
      </Box>

      <SearchSuratJalanModal
        isOpen={isSearchModalOpen}
        onClose={handleSearchModalClose}
        lhcId={lhc?.id ?? ''}
        onAdded={handleSearchModalAdded}
      />

      <SearchNomorMutasiModal
        isOpen={isSearchMutasiModalOpen}
        onClose={handleCloseSearchMutasiModal}
        lhcId={lhc?.id ?? ''}
        lhcNo={lhc?.lhc_no ?? ''}
        rowData={selectedRowForMutasi}
        onMutationSelected={handleMutationSelected}
        title={mutasiModalLabels.title}
        submitLabel={mutasiModalLabels.submitLabel}
        showUsedMutations={
          mutasiModalMode === 'change' || mutasiModalMode === 'add'
        }
      />

      <MutationDetailDrawer
        isOpen={isPaymentProofDrawerOpen}
        selectedMutation={selectedMutationForDrawer}
        rowData={selectedRowForDrawer}
        mutationDetailData={mutationDetailMutation.data ?? null}
        paymentProofDetailData={null}
        isLoading={mutationDetailMutation.isPending}
        onClose={handleClosePaymentProofDrawer}
      />

      <LHCMutationHistoryDrawer
        isOpen={isMutationHistoryDrawerOpen}
        onClose={closeMutationHistoryDrawer}
        data={mutationHistoryData ?? null}
        isLoading={isMutationHistoryLoading}
        isError={isMutationHistoryError}
        activeFilter={mutationHistoryFilter}
        onFilterChange={handleMutationHistoryFilterChange}
        onDateFilterChange={handleMutationHistoryDateFilter}
        onDateRangeChange={handleMutationHistoryDateRangeChange}
        onPageChange={handleMutationHistoryPageChange}
        onLimitChange={handleMutationHistoryLimitChange}
      />

      <RemarkLHCModal
        isOpen={isRemarkModalOpen}
        onClose={handleCloseRemarkModal}
        lhcId={lhc?.id ?? ''}
        rowData={selectedRowForRemark}
        onSaved={() => void refetchLHCDetail()}
      />

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Item LHC?
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus item ini? Tindakan ini tidak
              dapat dibatalkan.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onDeleteDialogClose}
                variant="outline"
              >
                Batal
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                isLoading={deleteItemMutation.isPending}
                ml={3}
              >
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  );
}

export default LHCDetail;
